const DEFAULT_OPTIONS = {
  maxWidth: 720,
  maxHeight: 1280,
  frameRate: 24,
  minVideoBitsPerSecond: 450_000,
  maxVideoBitsPerSecond: 900_000,
  audioBitsPerSecond: 96_000,
};

function getSupportedMimeType() {
  if (typeof MediaRecorder === "undefined") return "";

  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];

  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || "";
}

function waitForEvent(target, eventName) {
  return new Promise((resolve, reject) => {
    const handleSuccess = () => {
      cleanup();
      resolve();
    };

    const handleError = () => {
      cleanup();
      reject(new Error(`Failed while waiting for ${eventName}`));
    };

    const cleanup = () => {
      target.removeEventListener(eventName, handleSuccess);
      target.removeEventListener("error", handleError);
    };

    target.addEventListener(eventName, handleSuccess, { once: true });
    target.addEventListener("error", handleError, { once: true });
  });
}

function calculateScaledDimensions(width, height, maxWidth, maxHeight) {
  const scale = Math.min(maxWidth / width, maxHeight / height, 1);

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function stopTracks(stream) {
  if (!stream) return;
  stream.getTracks().forEach((track) => track.stop());
}

export async function compressVideoBlob(inputBlob, options = {}) {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    typeof MediaRecorder === "undefined"
  ) {
    return inputBlob;
  }

  const settings = { ...DEFAULT_OPTIONS, ...options };
  const mimeType = getSupportedMimeType();

  if (!mimeType) return inputBlob;

  const video = document.createElement("video");
  const canvas = document.createElement("canvas");
  const sourceUrl = URL.createObjectURL(inputBlob);

  let sourceStream = null;
  let canvasStream = null;
  let composedStream = null;
  let animationFrameId = null;

  try {
    video.src = sourceUrl;
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;

    await waitForEvent(video, "loadedmetadata");

    const duration = Number.isFinite(video.duration) && video.duration > 0
      ? video.duration
      : 1;

    const { width, height } = calculateScaledDimensions(
      video.videoWidth || settings.maxWidth,
      video.videoHeight || settings.maxHeight,
      settings.maxWidth,
      settings.maxHeight
    );

    canvas.width = width;
    canvas.height = height;

    const originalBitrate = Math.round((inputBlob.size * 8) / duration);
    const targetVideoBitsPerSecond = Math.min(
      settings.maxVideoBitsPerSecond,
      Math.max(settings.minVideoBitsPerSecond, Math.round(originalBitrate * 0.65))
    );

    const context = canvas.getContext("2d", { alpha: false });
    if (!context) return inputBlob;

    canvasStream = canvas.captureStream(settings.frameRate);

    if (typeof video.captureStream === "function") {
      sourceStream = video.captureStream();
    } else if (typeof video.mozCaptureStream === "function") {
      sourceStream = video.mozCaptureStream();
    }

    const tracks = [...canvasStream.getVideoTracks()];
    if (sourceStream) {
      tracks.push(...sourceStream.getAudioTracks());
    }

    composedStream = new MediaStream(tracks);

    const compressedBlob = await new Promise((resolve, reject) => {
      const recorder = new MediaRecorder(composedStream, {
        mimeType,
        videoBitsPerSecond: targetVideoBitsPerSecond,
        audioBitsPerSecond: settings.audioBitsPerSecond,
      });
      const chunks = [];

      const drawFrame = () => {
        if (!video.paused && !video.ended) {
          context.drawImage(video, 0, 0, width, height);
          animationFrameId = window.requestAnimationFrame(drawFrame);
        }
      };

      recorder.ondataavailable = (event) => {
        if (event.data?.size) {
          chunks.push(event.data);
        }
      };

      recorder.onerror = () => {
        reject(new Error("Video compression failed"));
      };

      recorder.onstop = () => {
        resolve(new Blob(chunks, { type: mimeType || inputBlob.type || "video/webm" }));
      };

      video.onended = () => {
        if (animationFrameId) {
          window.cancelAnimationFrame(animationFrameId);
        }
        if (recorder.state !== "inactive") {
          recorder.stop();
        }
      };

      recorder.start(250);
      context.drawImage(video, 0, 0, width, height);
      drawFrame();

      video.play().catch(reject);
    });

    return compressedBlob.size > 0 && compressedBlob.size < inputBlob.size
      ? compressedBlob
      : inputBlob;
  } catch (error) {
    console.warn("Video compression skipped:", error);
    return inputBlob;
  } finally {
    if (animationFrameId) {
      window.cancelAnimationFrame(animationFrameId);
    }
    stopTracks(sourceStream);
    stopTracks(canvasStream);
    stopTracks(composedStream);
    video.pause();
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(sourceUrl);
  }
}
